import { api } from './api';

/**
 * Leituras do cache do feed do prefixo na Home. O scheduler "Home :: cache do feed do prefixo" recalcula
 * as duas collections de 2 em 2 horas; aqui só se busca o documento do prefixo (spec `home-feed-prefixo`).
 */

/**
 * Rosto de quem enviou o reconhecimento ou do dono da realização, como o scheduler grava: foto
 * (`player.image.small.url`), avatar montado (`player.extra.avatar`) e a escolha entre os dois
 * (`player.extra.avatar_type`). Ausentes em caches gravados antes da HOME-44 (reconhecimentos) e da AJH-52 (realizações).
 */
export interface RostoDoAutor {
  authorImage?: string | null;
  authorAvatar?: Record<string, unknown> | null;
  authorAvatarType?: number | null;
}

/** Rosto de quem recebeu o reconhecimento, nos mesmos campos do autor. Ausente em caches gravados antes da AJ3-24. */
export interface RostoDoDestinatario {
  recipientImage?: string | null;
  recipientAvatar?: Record<string, unknown> | null;
  recipientAvatarType?: number | null;
}

/** Reconhecimento como o scheduler grava: nomes e mensagem crus, formatados na tela. */
export interface CachedRecognition extends RostoDoAutor, RostoDoDestinatario {
  _id: string;
  time: number | string;
  author: string;
  recipient: string;
  message: string;
  behavior: string;
  points: number;
  /** Moeda escolhida (`valeu`, `mandou`, `uau`). Ausente em caches gravados antes da HOME-19. */
  coin?: string;
}

/** Realização como o scheduler grava, com os campos do cartão. */
export interface CachedPrefixAchievement extends RostoDoAutor {
  _id: string;
  faseTitle?: string;
  titulo?: string;
  nome_uor?: string;
  status?: string;
  total?: number;
  /** Data de cadastro, nome de guerra do dono e demais integrantes (HOME-31). Ausentes em caches antigos. */
  created?: number | string;
  author?: string;
  participantes?: number;
}

async function readCachedItems<T>(aggregate: string, params: Record<string, unknown>): Promise<T[]> {
  const { data } = await api.post(`find/${aggregate}`, params);
  const items = Array.isArray(data) ? data[0]?.items : undefined;
  return Array.isArray(items) ? items : [];
}

export function getPrefixRecognitions(prefixo: number) {
  return readCachedItems<CachedRecognition>('home_reconhecimentos_prefixo', { prefixo });
}

export function getPrefixAchievements(prefixo: number, cycleId: string) {
  return readCachedItems<CachedPrefixAchievement>('home_realizacoes_prefixo', { prefixo, cycleId });
}

/** Reconhecimentos por página da lista "Reconhecimentos do prefixo" (AJ3-01). */
export const RECONHECIMENTOS_POR_PAGINA = 12;

/** Uma página dos reconhecimentos do prefixo, com o total do ciclo e o prefixo que o servidor usou. */
export interface PaginaDeReconhecimentos {
  itens: CachedRecognition[];
  total: number;
  prefixo?: number;
}

/**
 * Todos os reconhecimentos do ciclo enviados pelo prefixo de quem acessa, lidos ao vivo (AJ3-01). O prefixo vem do
 * token no servidor. A página vai no corpo, não no `Range`: o aggregate devolve um documento `{ total, prefixo, itens }`
 * e conta sem os lookups (com `Range`, o Funifier contava o pipeline inteiro).
 */
export async function getReconhecimentosDoPrefixo(cycleId: string, pagina: number): Promise<PaginaDeReconhecimentos> {
  const { data } = await api.post('find/reconhecimentos_do_prefixo', {
    cycleId,
    inicio: pagina * RECONHECIMENTOS_POR_PAGINA,
    quantidade: RECONHECIMENTOS_POR_PAGINA,
  });
  const documento = Array.isArray(data) ? data[0] : undefined;

  return {
    itens: Array.isArray(documento?.itens) ? documento.itens : [],
    total: Number.isFinite(documento?.total) ? documento.total : 0,
    prefixo: documento?.prefixo,
  };
}
